import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Badge,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Label,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@asm/ui";
import {
  ArrowLeft,
  Calendar,
  Clock,
  Play,
  Pause,
  PlayCircle,
  Trash2,
  Settings2,
  GitCompare,
  AlertTriangle,
  ExternalLink,
  CheckCircle,
  XCircle,
  SkipForward,
  Check,
  CalendarClock,
  Server,
  Mail,
  ShieldCheck,
  FileCode,
  Globe,
  Cloud,
  Network,
  Cpu,
  Github,
  Bot,
  Code,
  Hash,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  schedulesApi,
  type Schedule,
  type ScheduleExecution,
  type UpdateScheduleRequest,
} from "@/lib/api";
import { formatModuleName } from "@/lib/utils";

const MODULE_ICONS: Record<string, React.ElementType> = {
  dns: Server,
  email: Mail,
  tls: ShieldCheck,
  headers: FileCode,
  whois: Globe,
  cloud: Cloud,
  asn: Network,
  tech: Cpu,
  repos: Github,
  crawl_hints: Bot,
  js_analysis: Code,
  favicon: Hash,
};

const AVAILABLE_MODULES = [
  { id: "dns", name: "DNS", description: "DNS records and configuration" },
  { id: "email", name: "Email Security", description: "SPF, DKIM, DMARC analysis" },
  { id: "tls", name: "TLS/SSL", description: "Certificate analysis" },
  { id: "headers", name: "HTTP Headers", description: "Security headers analysis" },
  { id: "whois", name: "WHOIS", description: "Domain registration info" },
  { id: "cloud", name: "Cloud Assets", description: "S3, Azure, GCS bucket discovery" },
  { id: "asn", name: "ASN/BGP", description: "IP ranges and network intelligence" },
  { id: "tech", name: "Technology", description: "Web technology fingerprinting" },
  { id: "repos", name: "Repository Exposure", description: "GitHub/GitLab secret scanning" },
  { id: "crawl_hints", name: "Crawl Hints", description: "robots.txt, sitemap analysis" },
  { id: "js_analysis", name: "JS Analysis", description: "API endpoints, secrets detection" },
  { id: "favicon", name: "Favicon Hash", description: "Application fingerprinting" },
];

const FREQUENCIES = [
  { value: "daily", label: "Daily", interval: 1440 },
  { value: "weekly", label: "Weekly", interval: 10080 },
  { value: "monthly", label: "Monthly", interval: 43200 },
];

const HOURS = Array.from({ length: 24 }, (_, i) => ({
  value: i.toString(),
  label: `${i.toString().padStart(2, "0")}:00`,
}));

const DAYS_OF_WEEK = [
  { value: "0", label: "Sunday" },
  { value: "1", label: "Monday" },
  { value: "2", label: "Tuesday" },
  { value: "3", label: "Wednesday" },
  { value: "4", label: "Thursday" },
  { value: "5", label: "Friday" },
  { value: "6", label: "Saturday" },
];

function formatFrequency(schedule: Schedule): string {
  if (schedule.cron_expression) {
    return `Custom: ${schedule.cron_expression}`;
  }
  const interval = schedule.interval_minutes;
  if (interval === 60) return "Hourly";
  if (interval === 360) return "Every 6 hours";
  if (interval === 720) return "Every 12 hours";
  if (interval === 1440) return "Daily";
  if (interval === 10080) return "Weekly";
  return "Monthly";
}

function getFrequencyValue(schedule: Schedule): string {
  if (schedule.cron_expression) return "custom";
  const interval = schedule.interval_minutes;
  if (interval === 1440) return "daily";
  if (interval === 10080) return "weekly";
  return "monthly";
}

function getExecutionStatusBadge(status: string) {
  const config: Record<string, { className: string; icon: React.ReactNode }> = {
    queued: {
      className: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
      icon: <CheckCircle className="h-3 w-3 mr-1" />,
    },
    skipped_quota: {
      className: "bg-amber-500/20 text-amber-400 border-amber-500/30",
      icon: <SkipForward className="h-3 w-3 mr-1" />,
    },
    skipped_running: {
      className: "bg-muted text-muted-foreground border-border",
      icon: <SkipForward className="h-3 w-3 mr-1" />,
    },
    skipped_disabled: {
      className: "bg-muted text-muted-foreground border-border",
      icon: <SkipForward className="h-3 w-3 mr-1" />,
    },
    failed: {
      className: "bg-red-500/20 text-red-400 border-red-500/30",
      icon: <XCircle className="h-3 w-3 mr-1" />,
    },
  };
  const { className, icon } = config[status] || config.failed;
  return (
    <Badge className={`flex items-center w-fit border ${className}`}>
      {icon}
      {status.replace(/_/g, " ")}
    </Badge>
  );
}

export default function ScheduleDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation("schedules");
  const { t: tCommon } = useTranslation("common");

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [formData, setFormData] = useState<Partial<UpdateScheduleRequest>>({});
  const [selectedModules, setSelectedModules] = useState<string[]>([]);

  const { data: schedule, isLoading } = useQuery({
    queryKey: ["schedule", id],
    queryFn: () => schedulesApi.get(id!),
    enabled: !!id,
  });

  const { data: executionsData, isLoading: executionsLoading } = useQuery({
    queryKey: ["schedule-executions", id],
    queryFn: () => schedulesApi.getExecutions(id!, { per_page: 20 }),
    enabled: !!id,
  });

  const updateSchedule = useMutation({
    mutationFn: (data: UpdateScheduleRequest) => schedulesApi.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["schedule", id] });
      setEditDialogOpen(false);
    },
  });

  const pauseSchedule = useMutation({
    mutationFn: schedulesApi.pause,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["schedule", id] });
    },
  });

  const resumeSchedule = useMutation({
    mutationFn: schedulesApi.resume,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["schedule", id] });
    },
  });

  const triggerSchedule = useMutation({
    mutationFn: schedulesApi.trigger,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["schedule", id] });
      queryClient.invalidateQueries({ queryKey: ["schedule-executions", id] });
    },
  });

  const deleteSchedule = useMutation({
    mutationFn: schedulesApi.delete,
    onSuccess: () => {
      navigate("/schedules");
    },
  });

  const openEditDialog = () => {
    if (!schedule) return;
    setFormData({
      name: schedule.name,
      frequency: getFrequencyValue(schedule),
      preferred_hour: schedule.preferred_hour ?? undefined,
      preferred_day_of_week: schedule.preferred_day_of_week ?? undefined,
      auto_compare: schedule.auto_compare,
    });
    setSelectedModules(schedule.modules);
    setEditDialogOpen(true);
  };

  const toggleModule = (moduleId: string) => {
    setSelectedModules((prev) =>
      prev.includes(moduleId)
        ? prev.filter((m) => m !== moduleId)
        : [...prev, moduleId]
    );
  };

  const handleUpdate = (e: React.FormEvent) => {
    e.preventDefault();
    updateSchedule.mutate({
      ...formData,
      modules: selectedModules,
    });
  };

  if (isLoading) {
    return (
      <div className="tactical-grid space-y-6">
        <div className="animate-fade-in-up">
          <Skeleton className="h-10 w-48 rounded-lg" />
        </div>
        <div className="animate-fade-in-up stagger-1">
          <Skeleton className="h-64 w-full rounded-lg" />
        </div>
        <div className="animate-fade-in-up stagger-2">
          <Skeleton className="h-48 w-full rounded-lg" />
        </div>
      </div>
    );
  }

  if (!schedule) {
    return (
      <div className="tactical-grid flex items-center justify-center">
        <div className="text-center animate-fade-in-up">
          <div className="p-4 rounded-xl bg-muted/30 border border-border mb-6 inline-block">
            <CalendarClock className="h-12 w-12 text-muted-foreground" />
          </div>
          <p className="text-muted-foreground mb-4">{t("details.notFound", "Schedule not found")}</p>
          <Button variant="outline" asChild>
            <Link to="/schedules">
              <ArrowLeft className="mr-2 h-4 w-4" />
              {tCommon("buttons.back", "Back")}
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  const executions = executionsData?.executions || [];

  return (
    <div className="tactical-grid space-y-8">
      {/* Header */}
      <div className="animate-fade-in-up flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild className="hover:bg-muted/50">
            <Link to="/schedules">
              <ArrowLeft className="h-5 w-5" />
            </Link>
          </Button>
          <div className="p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
            <CalendarClock className="h-8 w-8 text-cyan-400" />
          </div>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold tracking-tight">{schedule.name}</h1>
              <Badge className={`border ${schedule.enabled
                ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                : "bg-muted text-muted-foreground border-border"}`}>
                {schedule.enabled ? "Active" : "Paused"}
              </Badge>
            </div>
            <p className="text-muted-foreground font-mono text-sm">
              {schedule.modules.length} {t("details.modules", "modules")} &bull; {formatFrequency(schedule)}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {schedule.enabled ? (
            <Button
              variant="outline"
              onClick={() => pauseSchedule.mutate(id!)}
              disabled={pauseSchedule.isPending}
              className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
            >
              <Pause className="mr-2 h-4 w-4" />
              {t("actions.pause", "Pause")}
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={() => resumeSchedule.mutate(id!)}
              disabled={resumeSchedule.isPending}
              className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
            >
              <Play className="mr-2 h-4 w-4" />
              {t("actions.resume", "Resume")}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => triggerSchedule.mutate(id!)}
            disabled={triggerSchedule.isPending}
            className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10"
          >
            <PlayCircle className="mr-2 h-4 w-4" />
            {t("actions.runNow", "Run Now")}
          </Button>
          <Button variant="outline" onClick={openEditDialog}>
            <Settings2 className="mr-2 h-4 w-4" />
            {tCommon("buttons.edit", "Edit")}
          </Button>
          <Button
            variant="outline"
            onClick={() => setDeleteDialogOpen(true)}
            className="border-red-500/30 text-red-400 hover:bg-red-500/10"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            {tCommon("buttons.delete", "Delete")}
          </Button>
        </div>
      </div>

      {/* Status Banner */}
      {!schedule.enabled && (
        <div className="animate-fade-in-up p-4 rounded-lg border border-amber-500/30 bg-amber-500/10 flex items-center gap-3">
          <Pause className="h-5 w-5 text-amber-400" />
          <span className="text-amber-400">
            {t("details.paused", "This schedule is currently paused and will not run automatically.")}
          </span>
        </div>
      )}

      {schedule.consecutive_failures > 0 && (
        <div className="animate-fade-in-up p-4 rounded-lg border border-red-500/30 bg-red-500/10 flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-red-400" />
          <span className="text-red-400">
            {t("details.failures", "This schedule has {{count}} consecutive failures.", { count: schedule.consecutive_failures })}
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-fade-in-up stagger-1">
        {/* Configuration Card */}
        <div className="lg:col-span-2 rounded-lg border border-border bg-card/50 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-6 w-1 rounded-full bg-cyan-500" />
            <h2 className="text-lg font-semibold">{t("details.configuration", "Configuration")}</h2>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-lg border border-border bg-muted/30 p-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{t("details.frequency", "Frequency")}</p>
                <p className="font-medium flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-cyan-400" />
                  {formatFrequency(schedule)}
                </p>
              </div>
              <div className="rounded-lg border border-border bg-muted/30 p-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{t("details.nextRun", "Next Run")}</p>
                <p className="font-medium flex items-center gap-2">
                  <Clock className="h-4 w-4 text-cyan-400" />
                  <span className="font-mono text-sm">{new Date(schedule.next_run_at).toLocaleString()}</span>
                </p>
              </div>
              {schedule.preferred_hour !== null && (
                <div className="rounded-lg border border-border bg-muted/30 p-4">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{t("details.preferredTime", "Preferred Time")}</p>
                  <p className="font-medium font-mono">{schedule.preferred_hour.toString().padStart(2, "0")}:00 {schedule.timezone}</p>
                </div>
              )}
              {schedule.preferred_day_of_week !== null && (
                <div className="rounded-lg border border-border bg-muted/30 p-4">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{t("details.preferredDay", "Preferred Day")}</p>
                  <p className="font-medium">{DAYS_OF_WEEK.find(d => d.value === schedule.preferred_day_of_week?.toString())?.label}</p>
                </div>
              )}
              <div className="rounded-lg border border-border bg-muted/30 p-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{t("details.lastRun", "Last Run")}</p>
                <p className="font-medium font-mono text-sm">
                  {schedule.last_run_at
                    ? new Date(schedule.last_run_at).toLocaleString()
                    : t("details.never", "Never")}
                </p>
              </div>
              <div className="rounded-lg border border-border bg-muted/30 p-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{t("details.lastStatus", "Last Status")}</p>
                <div className="font-medium">
                  {schedule.last_run_status
                    ? getExecutionStatusBadge(schedule.last_run_status)
                    : <span className="text-muted-foreground">—</span>}
                </div>
              </div>
            </div>

            <div className="border-t border-border pt-4">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3">{t("details.scanModules", "Scan Modules")}</p>
              <div className="flex flex-wrap gap-2">
                {schedule.modules.map((moduleId) => {
                  const Icon = MODULE_ICONS[moduleId] || Code;
                  return (
                    <Badge key={moduleId} className="border bg-cyan-500/10 text-cyan-400 border-cyan-500/30 flex items-center gap-1.5">
                      <Icon className="h-3 w-3" />
                      {formatModuleName(moduleId)}
                    </Badge>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Auto-Compare Card */}
        <div className="rounded-lg border border-border bg-card/50 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-purple-500/10 border border-purple-500/20">
              <GitCompare className="h-5 w-5 text-purple-400" />
            </div>
            <div>
              <h2 className="font-semibold">{t("details.autoCompare", "Auto-Compare")}</h2>
              <Badge className={`border text-xs ${schedule.auto_compare
                ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                : "bg-muted text-muted-foreground border-border"}`}>
                {schedule.auto_compare ? "Enabled" : "Disabled"}
              </Badge>
            </div>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            {schedule.auto_compare
              ? t("details.autoCompareEnabled", "Automatically compares each scan with the previous one")
              : t("details.autoCompareDisabled", "Auto-comparison is disabled")}
          </p>
          {schedule.auto_compare ? (
            schedule.last_comparison_id ? (
              <div className="space-y-4">
                <div className="p-4 rounded-lg border border-border bg-muted/30">
                  <p className="text-sm font-medium mb-3">{t("details.latestComparison", "Latest Comparison")}</p>
                  <Button variant="outline" asChild className="w-full border-purple-500/30 text-purple-400 hover:bg-purple-500/10">
                    <Link to={`/comparisons?id=${schedule.last_comparison_id}`}>
                      <ExternalLink className="mr-2 h-4 w-4" />
                      {t("details.viewComparison", "View Full Comparison")}
                    </Link>
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <GitCompare className="h-8 w-8 mx-auto mb-2 text-muted-foreground/50" />
                <p className="text-sm text-muted-foreground">{t("details.noComparison", "No comparison available yet. A comparison will be generated after the second scan execution.")}</p>
              </div>
            )
          ) : (
            <div className="text-center py-4">
              <p className="text-sm text-muted-foreground">{t("details.enableAutoCompare", "Enable auto-compare to automatically track changes between scans.")}</p>
            </div>
          )}
        </div>
      </div>

      {/* Execution History */}
      <div className="animate-fade-in-up stagger-2 rounded-lg border border-border bg-card/50 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-6 w-1 rounded-full bg-cyan-500" />
          <div>
            <h2 className="text-lg font-semibold">{t("details.executionHistory", "Execution History")}</h2>
            <p className="text-xs text-muted-foreground">{t("details.executionHistoryDesc", "Recent scheduled scan executions")}</p>
          </div>
        </div>
        {executionsLoading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full rounded-lg" />
            ))}
          </div>
        ) : executions.length ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-3 text-xs text-muted-foreground uppercase tracking-wider font-medium">{t("table.triggeredAt", "Triggered At")}</th>
                  <th className="text-left p-3 text-xs text-muted-foreground uppercase tracking-wider font-medium">{t("table.scheduledFor", "Scheduled For")}</th>
                  <th className="text-left p-3 text-xs text-muted-foreground uppercase tracking-wider font-medium">{t("table.status", "Status")}</th>
                  <th className="text-left p-3 text-xs text-muted-foreground uppercase tracking-wider font-medium">{t("table.scan", "Scan")}</th>
                </tr>
              </thead>
              <tbody>
                {executions.map((execution: ScheduleExecution, i: number) => (
                  <tr key={execution.id} className={`border-b border-border/50 hover:bg-muted/30 transition-colors animate-fade-in-up stagger-${Math.min(i + 1, 8)}`}>
                    <td className="p-3 font-mono text-sm text-muted-foreground">
                      {new Date(execution.triggered_at).toLocaleString()}
                    </td>
                    <td className="p-3 font-mono text-sm">
                      {new Date(execution.scheduled_for).toLocaleString()}
                    </td>
                    <td className="p-3">
                      {getExecutionStatusBadge(execution.status)}
                      {execution.skip_reason && (
                        <p className="text-xs text-muted-foreground mt-1">{execution.skip_reason}</p>
                      )}
                    </td>
                    <td className="p-3">
                      {execution.scan_id ? (
                        <Button variant="ghost" size="sm" asChild className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10">
                          <Link to={`/scans/${execution.scan_id}`}>
                            <ExternalLink className="h-4 w-4 mr-1" />
                            {t("table.viewScan", "View")}
                          </Link>
                        </Button>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="p-4 rounded-xl bg-muted/30 border border-border mb-4 inline-block">
              <Clock className="h-12 w-12 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground">{t("details.noExecutions", "No executions yet")}</p>
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-card border-border">
          <form onSubmit={handleUpdate}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
                  <Settings2 className="h-5 w-5 text-cyan-400" />
                </div>
                {t("edit.title", "Edit Schedule")}
              </DialogTitle>
              <DialogDescription>
                {t("edit.description", "Update the schedule configuration")}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-6">
              {updateSchedule.error && (
                <div className="p-3 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  {updateSchedule.error instanceof Error
                    ? updateSchedule.error.message
                    : t("edit.failed", "Failed to update schedule")}
                </div>
              )}

              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name" className="text-muted-foreground">{t("form.name", "Schedule Name")}</Label>
                <Input
                  id="name"
                  value={formData.name || ""}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="bg-muted/50 border-border"
                />
              </div>

              {/* Frequency */}
              <div className="space-y-2">
                <Label className="text-muted-foreground">{t("form.frequency", "Frequency")}</Label>
                <Select
                  value={formData.frequency}
                  onValueChange={(value) => setFormData({ ...formData, frequency: value })}
                >
                  <SelectTrigger className="bg-muted/50 border-border">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FREQUENCIES.map((freq) => (
                      <SelectItem key={freq.value} value={freq.value}>
                        {freq.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Preferred Time */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-muted-foreground">{t("form.preferredTime", "Preferred Time (UTC)")}</Label>
                  <Select
                    value={formData.preferred_hour?.toString()}
                    onValueChange={(value) => setFormData({ ...formData, preferred_hour: parseInt(value) })}
                  >
                    <SelectTrigger className="bg-muted/50 border-border font-mono">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {HOURS.map((hour) => (
                        <SelectItem key={hour.value} value={hour.value}>
                          {hour.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {formData.frequency === "weekly" && (
                  <div className="space-y-2">
                    <Label className="text-muted-foreground">{t("form.preferredDay", "Preferred Day")}</Label>
                    <Select
                      value={formData.preferred_day_of_week?.toString() || "1"}
                      onValueChange={(value) => setFormData({ ...formData, preferred_day_of_week: parseInt(value) })}
                    >
                      <SelectTrigger className="bg-muted/50 border-border">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DAYS_OF_WEEK.map((day) => (
                          <SelectItem key={day.value} value={day.value}>
                            {day.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {/* Module Selection */}
              <div className="space-y-3">
                <Label className="text-muted-foreground">{t("form.modules", "Scan Modules")}</Label>
                <div className="grid grid-cols-2 gap-2">
                  {AVAILABLE_MODULES.map((module) => {
                    const isSelected = selectedModules.includes(module.id);
                    const Icon = MODULE_ICONS[module.id] || Code;
                    return (
                      <button
                        key={module.id}
                        type="button"
                        onClick={() => toggleModule(module.id)}
                        className={`module-card p-3 rounded-lg text-left ${isSelected ? "selected" : ""}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Icon className={`h-4 w-4 ${isSelected ? "text-cyan-400" : "text-muted-foreground"}`} />
                            <span className="font-medium text-sm">{formatModuleName(module.id)}</span>
                          </div>
                          {isSelected && (
                            <Check className="h-4 w-4 text-cyan-400" />
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 ml-6">
                          {module.description}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Auto-Compare Toggle */}
              <div className="flex items-center justify-between rounded-lg border border-border bg-muted/30 p-4">
                <div className="space-y-0.5">
                  <Label className="text-base">{t("form.autoCompare", "Auto-Compare")}</Label>
                  <p className="text-sm text-muted-foreground">
                    {t("form.autoCompareDescription", "Automatically compare each scan with the previous execution")}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setFormData({ ...formData, auto_compare: !formData.auto_compare })}
                  className={`min-w-[80px] ${formData.auto_compare
                    ? "bg-cyan-500/20 text-cyan-400 border-cyan-500/30"
                    : ""}`}
                >
                  {formData.auto_compare && <Check className="mr-1 h-4 w-4" />}
                  {formData.auto_compare ? "On" : "Off"}
                </Button>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditDialogOpen(false)}
              >
                {tCommon("buttons.cancel", "Cancel")}
              </Button>
              <Button
                type="submit"
                disabled={selectedModules.length === 0 || updateSchedule.isPending}
                className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold"
              >
                {updateSchedule.isPending
                  ? t("edit.saving", "Saving...")
                  : t("edit.save", "Save Changes")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-500/10 border border-red-500/20">
                <Trash2 className="h-5 w-5 text-red-400" />
              </div>
              {t("delete.title", "Delete Schedule")}
            </DialogTitle>
            <DialogDescription>
              {t("delete.confirmation", "Are you sure you want to delete this schedule? This action cannot be undone.")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              {tCommon("buttons.cancel", "Cancel")}
            </Button>
            <Button
              variant="outline"
              onClick={() => deleteSchedule.mutate(id!)}
              disabled={deleteSchedule.isPending}
              className="bg-red-500/20 text-red-400 border-red-500/30 hover:bg-red-500/30"
            >
              {deleteSchedule.isPending
                ? t("delete.deleting", "Deleting...")
                : tCommon("buttons.delete", "Delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
